import {
	Spinner,
	Button,
	Typography,
	CustomSelect,
	FormContainer as Form,
	FormActions,
	FormValues,
} from '@stern-ui/ui';
import { Field, FormikHelpers, FormikProps } from 'formik';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { SelectField, TextAreaField } from '@/components/Form';
import { IconRadioGroup } from '@/components/Form/IconRadioGroup';
import { addNotification } from '@/components/Notifications/notificationsSlice';
import { selectPatientId, selectStudyId } from '@/components/PatientDataTable';
import {
	CheckScreeningRequiredStatus,
	useFetchPatientDetailsQuery,
	util,
} from '@/features/patientDetails';
import { patientListApi } from '@/features/patientLists';
import { reportingApi } from '@/features/reporting';
import { CancelledStatus, optOutOptions } from '@/globalConsts';

import {
	useCommitScreeningResultMutation,
	useFetchTrialSiteQuery,
	useSavePatientScreeningResultMutation,
} from '../../api/patientScreeningApi';
import { radioButtons } from '../../consts';

const schema = Yup.object().shape({
	screeningStatus: Yup.string().label('screeningStatus').required(),
	trialSite: Yup.string()
		.nullable(true)
		.label('trialSite')
		.when('screeningStatus', {
			is: '200',
			then: Yup.string().label('trialSite').required(),
		}),
	comments: Yup.string()
		.label('comments')
		.max(120)
		.when('screeningStatus', {
			is: '200',
			then: Yup.string().label('comments').required(),
		}),
	opt_out_reason: Yup.string()
		.label('Opt out reason')
		.when('screeningStatus', {
			is: (value: string) => value === '1000',
			then: Yup.string().required('Opt out reason is required'),
		}),
	opt_out_reason_other: Yup.string()
		.label('Opt out reason other')
		.when(['screeningStatus', 'opt_out_reason'], {
			is: (screeningStatus: string, opt_out_reason: string) => {
				return screeningStatus === '1000' && opt_out_reason === 'other';
			},
			then: Yup.string().required('Other reason for opting out is required'),
		}),
});
type CompleteScreeningFormParams = {
	patient_identifier: string;
};

export const CompleteScreeningForm = () => {
	const dispatch = useDispatch();
	const { patient_identifier: patient_id } = useParams<CompleteScreeningFormParams>();
	const patientIdFromState = useSelector(selectPatientId);
	const navigate = useNavigate();
	const studyId = useSelector(selectStudyId);
	const { data, isLoading } = useFetchTrialSiteQuery({ study_id: studyId });
	const { data: patientData, isLoading: patientLoading } = useFetchPatientDetailsQuery(
		patientIdFromState + ''
	);
	const [saveMaualScreeningResult, { isSuccess: savePatientScreeningSuccess }] =
		useSavePatientScreeningResultMutation();
	const [
		commitScreeningResult,
		{ isSuccess: commitScreeningResultSuccess, data: commitPatientData },
	] = useCommitScreeningResultMutation();

	const initialValues: FormValues = {
		screeningStatus:
			patientData && patientData.screening_status ? patientData.screening_status : '',
		trialSite: patientData && patientData.study_site_id ? patientData.study_site_id : '',
		comments: patientData && patientData.trial_site_note ? patientData.trial_site_note : '',
		opt_out_reason: patientData && patientData.opt_out_reason ? patientData.opt_out_reason : '',
		opt_out_reason_other:
			patientData && patientData.opt_out_reason_other ? patientData.opt_out_reason_other : '',
	};
	const handleSave = async (values: FormValues) => {
		await saveMaualScreeningResult({
			patientId: patient_id + '',
			study_site_id: values.trialSite !== '' ? (values.trialSite as string) : null,
			screening_status: values.screeningStatus as number,
			...(values.comments !== '' && {
				trial_site_note: values.comments + '',
			}),
			opt_out_reason: (values.opt_out_reason as string) || '',
			opt_out_reason_other: (values.opt_out_reason_other as string) || '',
		});
	};
	const screeningStatus = patientData
		? CheckScreeningRequiredStatus(
				patientData.status,
				patientData.status_label,
				patientData.cancelled,
				patientData.cancelled_label
			)
		: '';
	useEffect(() => {
		if (
			!patientIdFromState ||
			patientIdFromState !== patient_id ||
			!['Screening Required', 'Re-Screening Required'].includes(screeningStatus)
		) {
			navigate('../../', { relative: 'path' });
		}
	}, [
		navigate,
		patient_id,
		commitScreeningResultSuccess,
		patientIdFromState,
		commitPatientData,
		screeningStatus,
	]);
	useEffect(() => {
		if (commitPatientData && commitScreeningResultSuccess) {
			dispatch(
				addNotification({
					type: 'success',
					title: 'Success',
					message: 'You have successfully committed the patient screening result.',
					timeoutLength: 8000,
				})
			);
			if (commitPatientData.cancelled === CancelledStatus.OptedOut) {
				// patient is opted out
				// invalidate the patient list API
				dispatch(patientListApi.util.invalidateTags(['patient_list']));
				dispatch(reportingApi.util.invalidateTags(['cancelled_patient_list']));
				navigate('../../', { relative: 'path' });
			} else {
				dispatch(util.invalidateTags(['patient_details', 'person_details']));
				navigate('../', { relative: 'path' });
			}
		}
	}, [commitPatientData, commitScreeningResultSuccess, dispatch, navigate, patient_id]);
	useEffect(() => {
		if (savePatientScreeningSuccess) {
			dispatch(
				addNotification({
					type: 'success',
					title: 'Success',
					message: 'You have successfully saved the patient screening result.',
					timeoutLength: 8000,
				})
			);
		}
	}, [dispatch, navigate, patient_id, savePatientScreeningSuccess]);

	return (
		<div className='p-6 border-2 border-secondaryBorder rounded col-span-3'>
			<>
				<Typography variant='title1' className='mb-4' dataTestId='CSFtitle'>
					Please enter result of patient manual screening
				</Typography>
				<Typography
					variant='body'
					className='w-1/2 text-secondaryTextDark'
					dataTestId='CSFsubtitle'
				>
					By clicking on the commit button, the information will be forwarded to the trial
					site if it exists in the study
				</Typography>
			</>
			{patientLoading ? (
				<div className='flex flex-row justify-center items-center align-middle h-[640px]'>
					<Spinner variant='light' size='xl' className='' />
				</div>
			) : null}
			<Form
				initialValues={initialValues}
				dataTestId='form-complete-screening'
				onSubmit={async (values: FormValues) => {
					await handleSave(values);
					dispatch(util.invalidateTags(['patient_details', 'person_details']));
					navigate(`/patient-screening/${patient_id}`);
				}}
				schema={schema}
			>
				{(
					formikProps: FormikProps<FormValues> & FormActions & FormikHelpers<FormValues>
				) => {
					const { isSubmitting, isValid, setFieldValue, values, dirty } = formikProps;
					const isOptOutReasonOther = (values as FormValues).opt_out_reason === 'other';

					const resetOptOutReasonOtherField = (value: string) => {
						if (value !== 'other') {
							formikProps.setFieldValue('opt_out_reason_other', '');
						}
					};

					return (
						<>
							<div className='py-10 w-2/3'>
								<IconRadioGroup
									IconRadioButtons={radioButtons}
									selected={radioButtons.find(
										(elm) => elm.value === values.screeningStatus
									)}
									label='Screening Status'
									className='flex flex-row'
									onChange={(e) => {
										if (e.value !== 200) {
											setFieldValue('trialSite', '');
											setFieldValue('comments', '');
										}
										if (e.value !== 1000) {
											setFieldValue('opt_out_reason', '');
											setFieldValue('opt_out_reason_other', '');
										}
										setFieldValue('screeningStatus', e.value);
									}}
								></IconRadioGroup>
								{values.screeningStatus === 200 || values.screeningStatus == '' ? (
									<div className='my-8 w-2/3'>
										{!isLoading && data ? (
											<div className='my-8'>
												<SelectField
													type='text'
													label='Trial site'
													name='trialSite'
													dataTestId='select'
													disabled={values.screeningStatus !== 200}
													width='col6'
													options={data.map(
														(elm: { name: string; id: string }) => {
															return {
																label: elm.name,
																value: elm.id,
															};
														}
													)}
													handleChange={(
														e: React.ChangeEvent<HTMLSelectElement>
													) => {
														setFieldValue('trialSite', e.target.value);
													}}
												/>
											</div>
										) : null}
										<TextAreaField
											label='Comments to trial site'
											name='comments'
											disabled={values.screeningStatus !== 200}
											placeholder='Describe reason for contact and action that should be taken'
											className='mb-1'
											rows={4}
											width='col2'
											maxLength={120}
											dataTestId='textArea'
										/>
									</div>
								) : null}
								{values.screeningStatus === 1000 ? (
									<div className='my-8 w-2/3'>
										<div className='mt-6 max-w-96'>
											<Field
												className='custom-select'
												name='opt_out_reason'
												options={optOutOptions}
												component={CustomSelect}
												label={'Reason'}
												placeholder='Select'
												handleChange={resetOptOutReasonOtherField}
											/>
										</div>
										{isOptOutReasonOther && (
											<div className='mt-8 max-w-lg'>
												<TextAreaField
													label='Additional details'
													name='opt_out_reason_other'
												/>
											</div>
										)}
									</div>
								) : null}
							</div>
							<div className='pt-5'>
								<div className='flex flex-row justify-end'>
									<Button
										className='mr-4'
										type='submit'
										isLoading={isSubmitting}
										dataTestId='CSFSaveButton'
										disabled={!isValid || !dirty || isSubmitting}
									>
										Save
									</Button>
									<Button
										type='button'
										disabled={
											!isValid ||
											isSubmitting ||
											Object.values(values).every((elm) => elm === '')
										}
										dataTestId='CSFCommitButton'
										onClick={async () => {
											await handleSave(values);
											await commitScreeningResult(patient_id + '');
										}}
									>
										Commit
									</Button>
								</div>
							</div>
						</>
					);
				}}
			</Form>
		</div>
	);
};
